import { supabase } from '@/integrations/supabase/client';

export interface CreatePageContentInput {
  workbook_id: string;
  title: string;
  content?: string;
  type?: string;
  writing_style?: string;
  initial_request?: string;
  goalThreadId: string; // Required: use goal's thread for shared context
  researchSummaries?: Array<{
    title: string;
    summary: string;
  }>;
  clarifyingQuestions?: Array<{
    question: string;
    answer: string;
  }>;
}

export interface UpdatePageContentInput {
  title?: string;
  content?: string;
  type?: string;
  writing_style?: string;
  initial_request?: string;
  openai_thread_id?: string;
  display_order?: number;
}

export interface PageContent {
  id: string;
  workbook_id: string;
  title: string;
  content: string;
  type?: string;
  writing_style?: string;
  initial_request?: string;
  display_order?: number;
  created_at: string;
  updated_at: string;
  user_id: string;
  openai_thread_id?: string;
}

/**
 * Get all pages for a workbook
 * SCALABLE: Includes pages from workbooks where user is owner OR editor
 */
export async function getPagesByWorkbookId(workbookId: string): Promise<PageContent[]> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    throw new Error('User must be authenticated to fetch pages');
  }

  const userId = sessionData.session.user.id;

  // STEP 1: Check if user has access to this workbook (owner OR editor)
  const { data: workbookData, error: workbookError } = await supabase
    .from('workbook')
    .select('user_id')
    .eq('id', workbookId)
    .single();

  if (workbookError) {
    console.error('Error checking workbook access:', workbookError);
    throw workbookError;
  }

  const isOwner = workbookData?.user_id === userId;
  let isEditor = false;

  // If not owner, check if user is an editor
  if (!isOwner) {
    const { data: editorData, error: editorError } = await supabase
      .from('workbook_editors')
      .select('id')
      .eq('workbook_id', workbookId)
      .eq('editor_id', userId)
      .maybeSingle();

    if (editorError) {
      console.error('Error checking editor access:', editorError);
      // Continue without editor access
    } else {
      isEditor = !!editorData;
    }
  }

  // STEP 2: Verify access before fetching pages
  if (!isOwner && !isEditor) {
    console.warn(`[getPagesByWorkbookId] User ${userId} has no access to workbook ${workbookId}`);
    return []; // Return empty array instead of throwing error for better UX
  }

  console.log(`[getPagesByWorkbookId] User ${userId} has access to workbook ${workbookId} (owner: ${isOwner}, editor: ${isEditor})`);

  // STEP 3: Fetch pages for accessible workbook
  const { data, error } = await supabase
    .from('workbook_page_content')
    .select('*')
    .eq('workbook_id', workbookId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching pages:', error);
    throw error;
  }

  console.log(`[getPagesByWorkbookId] Retrieved ${data?.length || 0} pages for workbook ${workbookId}`);
  return data || [];
}

/**
 * Get a single page by ID
 */
export async function getPageContentById(id: string): Promise<PageContent | null> {
  const { data, error } = await supabase
    .from('workbook_page_content')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching page:', error);
    throw error;
  }

  return data;
}

/**
 * Hook-based version of page content creation with credit checking
 * Use this function with useAIOperationWrapper for proper credit management
 */
export function usePageContentGeneration() {
  return {
    createPageContentWithWrapper: async (
      input: CreatePageContentInput,
      executeAIOperation: (operation: () => Promise<any>, options?: any) => Promise<any>
    ): Promise<PageContent> => {
      console.log('[createPageContentWithWrapper] Starting page creation with credit checking:', input);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('You must be logged in to create pages');
      }

      try {
        // 🆕 UNIVERSAL FLOW: Use dedicated thread for this specific page
        const threadId = input.goalThreadId; // This is now always a dedicated page thread
        console.log('[createPageContentWithWrapper] Using dedicated page thread:', threadId);

        // Step 1: Generate content using the dedicated thread with credit checking
        console.log('[createPageContentWithWrapper] Generating content with credit check...');
        console.log('[createPageContentWithWrapper] Research summaries:', input.researchSummaries?.length || 0);
        console.log('[createPageContentWithWrapper] Clarifying questions:', input.clarifyingQuestions?.length || 0);
        console.log('[createPageContentWithWrapper] Writing style:', input.writing_style);
        console.log('[createPageContentWithWrapper] Page type:', input.workbook_id ? 'project' : 'private');
        
        const contentResponse = await executeAIOperation(
          async () => {
            const { data, error } = await supabase.functions.invoke('threaded-text-generation', {
              body: {
                type: 'page-content-generation',
                thread_id: threadId, // ✅ UNIVERSAL: Dedicated thread for this page
                pageTitle: input.title,
                pageType: input.type || 'Article',
                appName: 'Content Generator',
                functionName: 'Page Generation',
                researchSummaries: input.researchSummaries || [],
                clarifyingQuestions: input.clarifyingQuestions || [],
                writingStyle: input.writing_style,
                initial_request: input.initial_request
              }
            });

            if (error) {
              console.error('[createPageContentWithWrapper] Content generation failed:', error);
              throw new Error(`Failed to generate content: ${error.message}`);
            }

            return data;
          },
          {
            immediate: true,
            errorPrefix: 'Page content generation failed',
            debug: false
          }
        );

        const rawContent = contentResponse.content;
        console.log('[createPageContentWithWrapper] Raw content generated, length:', rawContent?.length);

        // Clean code fences immediately after receiving AI response (same pattern as useAITextGeneration)
        const generatedContent = rawContent
          ? rawContent
              .replace(/^```html\s*\n?/gm, '')
              .replace(/^```\s*\n?/gm, '')
              .replace(/\n?```$/gm, '')
              .trim()
          : '';

        // Log the cleaning process for debugging
        if (rawContent && rawContent !== generatedContent) {
          console.log('🧹 Page creation content cleaning:', {
            raw: rawContent.substring(0, 50) + '...',
            cleaned: generatedContent.substring(0, 50) + '...',
            hadCodeFences: rawContent.includes('```')
          });
        }

        console.log('[createPageContentWithWrapper] Content cleaned, final length:', generatedContent?.length);

        // Step 2: Create page record in database with dedicated thread
        const { data, error } = await supabase
          .from('workbook_page_content')
          .insert({
            workbook_id: input.workbook_id, // Can be null for private pages
            title: input.title,
            content: generatedContent || input.content || '',
            type: input.type,
            writing_style: input.writing_style,
            initial_request: input.initial_request,
            user_id: user.id,
            openai_thread_id: threadId, // ✅ UNIVERSAL: Dedicated thread for this page
          })
          .select()
          .single();

        if (error) {
          console.error('[createPageContentWithWrapper] Database insertion failed:', error);
          throw error;
        }

        console.log('[createPageContentWithWrapper] Page created successfully:', data.id);
        return data;

      } catch (error) {
        console.error('[createPageContentWithWrapper] Error in page creation:', error);
        throw error;
      }
    }
  };
}

/**
 * Create a new page with AI-generated content using threaded generation
 * @deprecated Use usePageContentGeneration hook with useAIOperationWrapper instead
 */
export async function createPageContent(input: CreatePageContentInput): Promise<PageContent> {
  console.warn('[DEPRECATED] createPageContent called directly. Use usePageContentGeneration hook with useAIOperationWrapper for credit checking.');
  
  console.log('[createPageContent] Starting page creation with threaded generation:', input);

  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) {
    throw new Error('You must be logged in to create pages');
  }

  try {
    // 🆕 UNIVERSAL FLOW: Use dedicated thread for this specific page
    const threadId = input.goalThreadId; // This is now always a dedicated page thread
    console.log('[createPageContent] Using dedicated page thread:', threadId);

    // Step 1: Generate content using the dedicated thread
    console.log('[createPageContent] Generating content...');
    console.log('[createPageContent] Research summaries:', input.researchSummaries?.length || 0);
    console.log('[createPageContent] Clarifying questions:', input.clarifyingQuestions?.length || 0);
    console.log('[createPageContent] Writing style:', input.writing_style);
    console.log('[createPageContent] Page type:', input.workbook_id ? 'project' : 'private');
    
    const contentResponse = await supabase.functions.invoke('threaded-text-generation', {
      body: {
        type: 'page-content-generation',
        thread_id: threadId, // ✅ UNIVERSAL: Dedicated thread for this page
        pageTitle: input.title,
        pageType: input.type || 'Article',
        appName: 'Content Generator',
        functionName: 'Page Generation',
        researchSummaries: input.researchSummaries || [],
        clarifyingQuestions: input.clarifyingQuestions || [],
        writingStyle: input.writing_style,
        initial_request: input.initial_request
      }
    });

    if (contentResponse.error) {
      console.error('[createPageContent] Content generation failed:', contentResponse.error);
      throw new Error(`Failed to generate content: ${contentResponse.error.message}`);
    }

    const rawContent = contentResponse.data.content;
    console.log('[createPageContent] Raw content generated, length:', rawContent?.length);

    // Clean code fences immediately after receiving AI response (same pattern as useAITextGeneration)
    const generatedContent = rawContent
      ? rawContent
          .replace(/^```html\s*\n?/gm, '')
          .replace(/^```\s*\n?/gm, '')
          .replace(/\n?```$/gm, '')
          .trim()
      : '';

    // Log the cleaning process for debugging
    if (rawContent && rawContent !== generatedContent) {
      console.log('🧹 Page creation content cleaning:', {
        raw: rawContent.substring(0, 50) + '...',
        cleaned: generatedContent.substring(0, 50) + '...',
        hadCodeFences: rawContent.includes('```')
      });
    }

    console.log('[createPageContent] Content cleaned, final length:', generatedContent?.length);

    // Step 2: Create page record in database with dedicated thread
    const { data, error } = await supabase
      .from('workbook_page_content')
      .insert({
        workbook_id: input.workbook_id, // Can be null for private pages
        title: input.title,
        content: generatedContent || input.content || '',
        type: input.type,
        writing_style: input.writing_style,
        initial_request: input.initial_request,
        user_id: user.id,
        openai_thread_id: threadId, // ✅ UNIVERSAL: Dedicated thread for this page
      })
      .select()
      .single();

    if (error) {
      console.error('[createPageContent] Database insertion failed:', error);
      throw error;
    }

    console.log('[createPageContent] Page created successfully:', data.id);
    return data;

  } catch (error) {
    console.error('[createPageContent] Error in page creation:', error);
    throw error;
  }
}

/**
 * Update an existing page
 */
export async function updatePageContent(id: string, updates: UpdatePageContentInput): Promise<PageContent> {
  const { data, error } = await supabase
    .from('workbook_page_content')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating page:', error);
    throw error;
  }

  return data;
}

/**
 * Delete a page
 */
export async function deletePageContent(id: string): Promise<void> {
  const { error } = await supabase
    .from('workbook_page_content')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting page:', error);
    throw error;
  }
}
